import { Trash2Icon } from 'lucide-react'
import { useState } from 'react'

import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute } from '@/lib/format'

import { itemsOf, runAction, useStaffDeletions, useStaffMutation, type DeletionRow, type StaffAction } from '../api'
import { useStaff } from '../hooks'
import { SELECT_CLASS } from '../shared'
import {
  ActionNotice,
  LoadMore,
  ReasonDialog,
  ShortId,
  StaffGate,
  StaffLink,
  StateBadge,
} from '../ui'

type Pending = { row: DeletionRow; kind: 'deletion-cancel' | 'deletion-expedite' }

export function StaffDeletionsPage() {
  const staff = useStaff()
  const [state, setState] = useState('pending,running')
  const list = useStaffDeletions(state)
  const rows = itemsOf(list.data)
  const [pending, setPending] = useState<Pending | null>(null)
  const [notice, setNotice] = useState<{ title: string; notified: boolean | undefined } | null>(null)
  const act = useStaffMutation(({ action, reason }: { action: StaffAction; reason: string }) => runAction(action, reason))

  const canCancel = staff.can('deletion_cancel_verify')
  const canExpedite = staff.can('deletion_expedite')

  return (
    <StaffGate>
      <PageHeader eyebrow="Staff" title="Deletions" description="Pending and running deletion jobs across accounts." />
      <PageBody>
        <ActionNotice notice={notice} onDismiss={() => { setNotice(null) }} />
        <Card>
          <CardContent className="space-y-4">
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="del-state">State</Label>
              <select id="del-state" className={SELECT_CLASS} value={state} onChange={(e) => { setState(e.target.value) }}>
                <option value="pending,running">Pending and running</option>
                <option value="pending">Pending</option>
                <option value="running">Running</option>
                <option value="failed">Failed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {list.isError ? (
              <ErrorState error={list.error} title="Could not load deletions" onRetry={() => void list.refetch()} />
            ) : list.isPending ? (
              <TableSkeleton rows={4} columns={6} />
            ) : rows.length === 0 ? (
              <EmptyState icon={Trash2Icon} title="No deletion jobs" description="Nothing is queued in this state." />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead>Scope</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Requested</TableHead>
                        <TableHead>Runs after</TableHead>
                        <TableHead className="text-right">Rows</TableHead>
                        {canCancel ? <TableHead /> : null}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.id} data-testid="staff-deletion-row">
                          <TableCell className="max-w-[14rem] truncate">
                            {r.account_id === null ? '—' : <StaffLink to={`/app/staff/accounts/${r.account_id}`}>{r.account_name ?? r.account_id}</StaffLink>}
                          </TableCell>
                          <TableCell><Badge variant="outline">{r.scope}</Badge></TableCell>
                          <TableCell><ShortId id={r.target_id} /></TableCell>
                          <TableCell><StateBadge state={r.state} /></TableCell>
                          <TableCell className="text-xs text-muted-foreground tabular-nums">{formatAbsolute(r.requested_at)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground tabular-nums">{formatAbsolute(r.execute_after)}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.rows_deleted}</TableCell>
                          {canCancel ? (
                            <TableCell>
                              <div className="flex justify-end gap-2">
                                {r.cancellable ? (
                                  <Button size="sm" variant="outline" onClick={() => { setPending({ row: r, kind: 'deletion-cancel' }) }}>
                                    Cancel
                                  </Button>
                                ) : null}
                                {canExpedite && r.expeditable ? (
                                  <Button size="sm" variant="destructive" onClick={() => { setPending({ row: r, kind: 'deletion-expedite' }) }}>
                                    Expedite
                                  </Button>
                                ) : null}
                              </div>
                            </TableCell>
                          ) : null}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <LoadMore shown={rows.length} hasNext={list.hasNextPage} loading={list.isFetchingNextPage} onMore={() => void list.fetchNextPage()} />
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>

      {pending === null ? null : (
        <ReasonDialog
          title={pending.kind === 'deletion-cancel' ? 'Cancel this deletion?' : 'Expedite this deletion?'}
          description={
            pending.kind === 'deletion-cancel'
              ? 'The job is cancelled and the data stays. The account’s administrators are emailed with your reason.'
              : 'The job runs on the next pass instead of waiting out its grace window. This cannot be undone. The account’s administrators are emailed with your reason.'
          }
          confirmLabel={pending.kind === 'deletion-cancel' ? 'Cancel deletion' : 'Expedite deletion'}
          destructive={pending.kind === 'deletion-expedite'}
          onClose={() => { setPending(null) }}
          onConfirm={async (reason) => {
            const result = await act.mutateAsync({ action: { kind: pending.kind, id: pending.row.id }, reason })
            setNotice({ title: pending.kind === 'deletion-cancel' ? 'Deletion cancelled' : 'Deletion expedited', notified: result.notified })
          }}
        />
      )}
    </StaffGate>
  )
}
